// =============================================================================
// MeuDriver — M3: Componente de Certificado de Conclusão
// =============================================================================

import { useRef } from 'react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Printer, Award, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { CertificateRow } from '@/hooks/useCertificates'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CertificateCardProps {
  certificate: CertificateRow
  studentName?: string | null
  instructorName?: string | null
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function CertificateCard({
  certificate,
  studentName,
  instructorName,
}: CertificateCardProps) {
  const printRef = useRef<HTMLDivElement>(null)

  const issuedDate = format(parseISO(certificate.issued_at), "dd 'de' MMMM 'de' yyyy", {
    locale: ptBR,
  })

  function handlePrint() {
    window.print()
  }

  // Resolve nomes de diferentes shapes de query
  const resolvedStudentName =
    studentName ??
    (certificate.student as { full_name?: string | null } | null)?.full_name ??
    'Aluno'

  const resolvedInstructorName =
    instructorName ??
    (
      certificate.instructor as
        | { profile?: { full_name?: string | null } | null }
        | null
    )?.profile?.full_name ??
    'Instrutor'

  return (
    <>
      {/* Botão imprimir — oculto na impressão */}
      <div className="print:hidden mb-4 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrint}
          data-testid="print-certificate"
          className="gap-2"
        >
          <Printer className="h-4 w-4" />
          Imprimir / PDF
        </Button>
      </div>

      {/* Card do certificado */}
      <div
        ref={printRef}
        className="
          relative overflow-hidden rounded-2xl border-2 border-teal-200 bg-white
          p-8 shadow-sm text-center
          print:rounded-none print:border-[3px] print:border-teal-600
          print:shadow-none print:p-10 print:min-h-[297mm]
        "
        data-testid="certificate-card"
      >
        {/* Decoração de fundo */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, #0d9488 0px, #0d9488 1px, transparent 0px, transparent 50%)',
            backgroundSize: '20px 20px',
          }}
        />

        {/* Conteúdo */}
        <div className="relative z-10 space-y-6">
          {/* Ícone e título */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-teal-50 ring-4 ring-teal-100">
              <Award className="h-8 w-8 text-teal-600" />
            </div>
            <div>
              <p className="text-xs font-semibold tracking-widest text-teal-600 uppercase">
                MeuDriver
              </p>
              <h2 className="text-2xl font-black text-gray-900 mt-0.5">
                Certificado de Conclusão
              </h2>
            </div>
          </div>

          {/* Texto formal */}
          <div className="space-y-1 text-gray-600 text-sm leading-relaxed max-w-md mx-auto">
            <p>Certificamos que</p>
            <p className="text-2xl font-bold text-gray-900">{resolvedStudentName}</p>
            <p>concluiu com aproveitamento o programa de aulas práticas de direção ministradas por</p>
            <p className="text-lg font-semibold text-teal-700">{resolvedInstructorName}</p>
          </div>

          {/* Detalhes */}
          <div className="flex items-center justify-center gap-8 py-4 border-y border-dashed border-teal-200">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">{certificate.total_lessons}</p>
              <p className="text-xs text-muted-foreground mt-0.5">aulas concluídas</p>
            </div>
            <div className="h-8 w-px bg-teal-200" />
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">{certificate.total_hours}h</p>
              <p className="text-xs text-muted-foreground mt-0.5">horas totais</p>
            </div>
            <div className="h-8 w-px bg-teal-200" />
            <div className="text-center">
              <p className="text-xs font-mono font-semibold text-teal-700">
                {certificate.certificate_number}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">nº do certificado</p>
            </div>
          </div>

          {/* Data e verificação */}
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>Emitido em <span className="font-medium text-gray-700">{issuedDate}</span></p>
            <div className="flex items-center justify-center gap-1.5 text-teal-600">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-xs">Documento verificável em meudriver.com.br</span>
            </div>
          </div>
        </div>
      </div>

      {/* Estilos de impressão */}
      <style>{`
        @media print {
          body > *:not(.print-target) { display: none !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </>
  )
}
